import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { styled } from '@mui/system';
import { locationsProfilesActions } from '@actions';
import { useLocations, useTranslation } from '@hooks';
import { getCompanyAddress } from '@helpers/companies';
import { ProfileName } from '@constants/integrations';
import { RoutePath } from '@constants/routes';
import { InfoOutlinedIcon } from 'assets/images/icons';

const Container = styled('div', {
    shouldForwardProp: prop => prop !== 'isLoaded',
})(({ isLoaded }) => ({
    height: isLoaded ? 'auto' : '100%',
}));

const IFrameWrapper = styled('div', {
    shouldForwardProp: prop => prop !== 'height',
})(({ height }) => ({
    height: height ? height : '100%',
    width: 320,
    margin: '0 auto',
    '@media (max-width: 768px)': {
        width: 320,
        minHeight: 520,
    },
    '@media (max-width: 420px)': {
        width: '100%',
    },
}));

const PreviewMicrosite = ({ slug }) => {
    const [height, setHeight] = useState('100%');
    const [isLoaded, setIsLoaded] = useState(false);

    const { data: locationsProfiles } = useSelector(state => state.locationsProfiles);
    const { count, averageRating } = useSelector(state => state.microsites);

    const { watch } = useFormContext();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { locationsMap } = useLocations();
    const iframeRef = useRef(null);

    const locationId = watch('locationId');
    const links = watch('links');
    const socialMediaEnable = watch('socialMediaEnable');
    const logoUrl = watch('logoUrl');
    const showReviews = watch('showReviews');
    const subscriptionEnabled = watch('subscriptionEnabled');

    useEffect(() => {
        if (locationId) {
            dispatch(locationsProfilesActions.get(locationId));
        }
    }, [locationId]);

    useEffect(() => {
        const controller = new AbortController();
        const setPreviewData = event => {
            if (event.origin === process.env.REACT_APP_SURVEY_DOMAIN) {
                const message = event.data;
                if (message.type === 'pl-preview-iframe-loaded') {
                    setHeight(message.data.iframeHeight + 48);
                    setIsLoaded(true);
                }
            }
        };
        window.addEventListener('message', setPreviewData, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        const selectedLocation = locationsMap[locationId] || {};
        const hasInstagramLink = locationsProfiles.find(p => p.type === ProfileName.Instagram);
        const hasFacebookLink = locationsProfiles.find(p => p.type === ProfileName.Facebook);
        const hasYoutubeLink = locationsProfiles.find(p => p.type === ProfileName.YouTube);
        const hasXLink = locationsProfiles.find(p => p.type === ProfileName.X);
        const hasLinkedInLink = locationsProfiles.find(p => p.type === ProfileName.LinkedIn);
        const hasTiktokLink = locationsProfiles.find(p => p.type === ProfileName.Tiktok);

        iframeRef.current?.contentWindow?.postMessage(
            {
                type: 'pl-preview-microsite',
                data: {
                    logoUrl,
                    locationName: selectedLocation.locationName,
                    businessAddress: getCompanyAddress(selectedLocation.addressLine1, selectedLocation.city),
                    hasBusinessAddress: selectedLocation.hasBusinessAddress,
                    showReviews,
                    links,
                    socialMediaEnable,
                    subscriptionEnabled,
                    socialMediaLinks: {
                        facebook: hasFacebookLink,
                        instagram: hasInstagramLink,
                        youtube: hasYoutubeLink,
                        x: hasXLink,
                        linkedIn: hasLinkedInLink,
                        tiktok: hasTiktokLink,
                    },
                    reviews: {
                        count,
                        rating: averageRating,
                    },
                },
            },
            '*',
        );
    }, [
        locationId,
        locationsProfiles,
        links,
        socialMediaEnable,
        logoUrl,
        showReviews,
        subscriptionEnabled,
        count,
        averageRating,
    ]);

    return (
        <>
            <Container isLoaded={isLoaded}>
                <IFrameWrapper height={height}>
                    <iframe
                        ref={iframeRef}
                        allow="autoplay"
                        width="100%"
                        height="100%"
                        src={`${process.env.REACT_APP_SURVEY_DOMAIN}/preview/${slug}`}
                        frameBorder="0"
                    ></iframe>
                </IFrameWrapper>
            </Container>
            <Typography
                variant="caption"
                sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginTop: '16px', textAlign: 'center' }}
            >
                <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                    <InternalLink
                        to={RoutePath.AccountSettingsBrands}
                        sx={{ color: '#1F4C5C', cursor: 'pointer', textDecoration: 'none' }}
                    >
                        {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                    </InternalLink>
                </Trans>
            </Typography>
        </>
    );
};

export default PreviewMicrosite;
